import { Credit } from "@/interfaces/credit";
import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { ToastInterface, useToast } from "vue-toastification";

interface Props {
  immediate?: boolean;
  params: Params;
  config?: Config;
}

interface Config {
  errorHandling: boolean;
}

interface Params {
  id: string | number;
}

interface CreateParams {
  id: string | number;
  limit: string;
  legalRepresentativeName: string;
  legalRepresentativeEmail: string;
  deadline: string;
}

const getHotelCredit = async (
  params: Params,
  errorHandling = true,
): Promise<Credit> => {
  const { data } = await axios.get<Credit>(
    `/api/internal/hotels/${params.id}/credits`,
    { errorHandling } as CustomAxiosRequestConfig,
  );

  return data;
};

const createHotelCredit = async (
  createParams: CreateParams,
  toast: ToastInterface,
) => {
  try {
    const { data } = await axios.post(
      `/api/internal/hotels/${createParams.id}/credits`,
      createParams,
    );

    toast.success("Crédito creado correctamente");
    return data;
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const useHotelCredit = (props: Props) => {
  const toast = useToast();
  const { params, immediate = true, config } = props;
  const { errorHandling = true } = config || {};

  const { data, isLoading, refetch } = useQuery(
    ["hotel-credit", params.id],
    () => getHotelCredit(params, errorHandling),
    {
      enabled: immediate,
      retry: false,
    },
  );

  const createMutation = useMutation((createParams: CreateParams) =>
    createHotelCredit(createParams, toast),
  );

  return {
    data,
    isLoading,
    refetch,

    createMutation,
    createMutationLoading: createMutation.isLoading,
    createMutationAsync: createMutation.mutateAsync,
  };
};

export default useHotelCredit;
